import React, { useState, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Typography,
    Stack,
    Stepper,
    Step,
    StepLabel,
    Box,
    Checkbox,
    FormControlLabel,
    Paper,
    IconButton,
    Chip,
    Slider,
    Tooltip
} from '@mui/material';
import { Brain, RefreshCw, Check, X, ExternalLink } from 'lucide-react';
import { firebaseService } from '../utils/FirebaseService';

const STEPS = ['Topic', 'Models', 'Evidences', 'References', 'Review'];

const MELWizard = ({ isOpen, onClose, onMELGenerated }) => {
    // Wizard state
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Topic step state
    const [topic, setTopic] = useState('');
    const [title, setTitle] = useState('');
    const [modelCount, setModelCount] = useState(3);
    const [evidenceCount, setEvidenceCount] = useState(6);

    // Models step state
    const [proposedModels, setProposedModels] = useState([]);
    const [selectedModels, setSelectedModels] = useState({});

    // Evidences step state
    const [proposedEvidences, setProposedEvidences] = useState([]);
    const [selectedEvidences, setSelectedEvidences] = useState({});

    // References step state
    const [evidencesWithRefs, setEvidencesWithRefs] = useState([]);

    // Reset wizard state
    const resetWizard = useCallback(() => {
        setActiveStep(0);
        setLoading(false);
        setError('');
        setTopic('');
        setTitle('');
        setModelCount(3);
        setEvidenceCount(6);
        setProposedModels([]);
        setSelectedModels({});
        setProposedEvidences([]);
        setSelectedEvidences({});
        setEvidencesWithRefs([]);
    }, []);

    const handleClose = () => {
        resetWizard();
        onClose();
    };

    // Step 1: Generate models
    const handleGenerateModels = async () => {
        if (!topic.trim()) {
            setError('Please enter a topic');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const models = await firebaseService.generateModels(topic, modelCount);
            setProposedModels(models);
            const initialSelection = {};
            models.forEach(m => { initialSelection[m.id] = true; });
            setSelectedModels(initialSelection);
            setActiveStep(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Regenerate rejected models
    const handleRegenerateModels = async () => {
        const approved = proposedModels.filter(m => selectedModels[m.id]);
        const rejectedCount = proposedModels.filter(m => !selectedModels[m.id]).length;
        if (rejectedCount === 0) return;

        setLoading(true);
        setError('');
        try {
            const newModels = await firebaseService.generateModels(topic, rejectedCount, approved);
            const combined = [...approved, ...newModels];
            setProposedModels(combined);
            const newSelection = {};
            combined.forEach(m => { newSelection[m.id] = true; });
            setSelectedModels(newSelection);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Generate evidences
    const handleGenerateEvidences = async () => {
        const approved = proposedModels.filter(m => selectedModels[m.id]);
        if (approved.length === 0) {
            setError('Please select at least one model');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const evidences = await firebaseService.generateEvidences(topic, approved, evidenceCount);
            setProposedEvidences(evidences);
            const initialSelection = {};
            evidences.forEach(e => { initialSelection[e.id] = true; });
            setSelectedEvidences(initialSelection);
            setActiveStep(2);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Regenerate rejected evidences
    const handleRegenerateEvidences = async () => {
        const approved = proposedEvidences.filter(e => selectedEvidences[e.id]);
        const rejectedCount = proposedEvidences.filter(e => !selectedEvidences[e.id]).length;
        if (rejectedCount === 0) return;

        setLoading(true);
        setError('');
        try {
            const approvedModels = proposedModels.filter(m => selectedModels[m.id]);
            const newEvidences = await firebaseService.generateEvidences(
                topic, approvedModels, rejectedCount, approved
            );
            const combined = [...approved, ...newEvidences];
            setProposedEvidences(combined);
            const newSelection = {};
            combined.forEach(e => { newSelection[e.id] = true; });
            setSelectedEvidences(newSelection);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Step 3: Generate and validate references
    const handleGenerateReferences = async () => {
        const approved = proposedEvidences.filter(e => selectedEvidences[e.id]);
        if (approved.length === 0) {
            setError('Please select at least one evidence');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const withRefs = await firebaseService.generateReferences(approved);
            const validated = await firebaseService.validateReferences(withRefs);
            setEvidencesWithRefs(validated);
            setActiveStep(3);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Step 4: Assemble and finish
    const handleAssembleMEL = async () => {
        setLoading(true);
        setError('');
        try {
            const approvedModels = proposedModels.filter(m => selectedModels[m.id]);
            const melTitle = title.trim() || topic.substring(0, 50);
            const melData = await firebaseService.assembleMEL(
                melTitle, topic, approvedModels, evidencesWithRefs
            );
            onMELGenerated(melData);
            handleClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getApprovedCount = (items, selection) =>
        items.filter(item => selection[item.id]).length;

    const getRejectedCount = (items, selection) =>
        items.filter(item => !selection[item.id]).length;

    // Render step content
    const renderStepContent = () => {
        switch (activeStep) {
            case 0: return renderTopicStep();
            case 1: return renderModelsStep();
            case 2: return renderEvidencesStep();
            case 3: return renderReferencesStep();
            case 4: return renderReviewStep();
            default: return null;
        }
    };

    const renderTopicStep = () => (
        <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary">
                Describe a topic to explore. The AI will generate competing models and evidence.
            </Typography>
            <TextField
                label="MEL Title (optional)"
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Causes of Climate Change"
                disabled={loading}
            />
            <TextField
                label="Topic Description"
                fullWidth
                multiline
                rows={3}
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Describe the scientific question or phenomenon to explore..."
                disabled={loading}
            />
            <Box sx={{ width: '100%', px: 1 }}>
                <Typography gutterBottom>Number of Models: {modelCount}</Typography>
                <Slider
                    value={modelCount}
                    onChange={(_, v) => setModelCount(v)}
                    min={2} max={4} step={1}
                    marks={[{value:2,label:'2'},{value:3,label:'3'},{value:4,label:'4'}]}
                    disabled={loading}
                />
            </Box>
            <Box sx={{ width: '100%', px: 1 }}>
                <Typography gutterBottom>Number of Evidences: {evidenceCount}</Typography>
                <Slider
                    value={evidenceCount}
                    onChange={(_, v) => setEvidenceCount(v)}
                    min={4} max={10} step={1}
                    marks={[{value:4,label:'4'},{value:6,label:'6'},{value:8,label:'8'},{value:10,label:'10'}]}
                    disabled={loading}
                />
            </Box>
        </Stack>
    );

    const renderModelsStep = () => (
        <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
                Review the proposed models. Uncheck any you want to reject and regenerate.
            </Typography>
            {proposedModels.map((model) => (
                <Paper key={model.id} sx={{ p: 2 }} elevation={1}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={selectedModels[model.id] || false}
                                onChange={(e) => setSelectedModels(prev => ({
                                    ...prev, [model.id]: e.target.checked
                                }))}
                                disabled={loading}
                            />
                        }
                        label={<Typography fontWeight="bold">{model.id}</Typography>}
                    />
                    <Typography variant="body2" sx={{ ml: 4 }}>{model.description}</Typography>
                </Paper>
            ))}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Chip
                    label={`${getApprovedCount(proposedModels, selectedModels)} approved`}
                    color="success" size="small"
                />
                {getRejectedCount(proposedModels, selectedModels) > 0 && (
                    <Chip
                        label={`${getRejectedCount(proposedModels, selectedModels)} to regenerate`}
                        color="warning" size="small"
                    />
                )}
            </Box>
        </Stack>
    );

    const renderEvidencesStep = () => (
        <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
                Review the proposed evidences. Uncheck any you want to reject and regenerate.
            </Typography>
            {proposedEvidences.map((evidence) => (
                <Paper key={evidence.id} sx={{ p: 2 }} elevation={1}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={selectedEvidences[evidence.id] || false}
                                onChange={(e) => setSelectedEvidences(prev => ({
                                    ...prev, [evidence.id]: e.target.checked
                                }))}
                                disabled={loading}
                            />
                        }
                        label={<Typography fontWeight="bold">{evidence.id}</Typography>}
                    />
                    <Typography variant="body2" sx={{ ml: 4 }}>{evidence.description}</Typography>
                </Paper>
            ))}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Chip
                    label={`${getApprovedCount(proposedEvidences, selectedEvidences)} approved`}
                    color="success" size="small"
                />
                {getRejectedCount(proposedEvidences, selectedEvidences) > 0 && (
                    <Chip
                        label={`${getRejectedCount(proposedEvidences, selectedEvidences)} to regenerate`}
                        color="warning" size="small"
                    />
                )}
            </Box>
        </Stack>
    );

    const renderReferencesStep = () => (
        <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
                References have been generated and validated. Review before final assembly.
            </Typography>
            {evidencesWithRefs.map((evidence) => (
                <Paper key={evidence.id} sx={{ p: 2 }} elevation={1}>
                    <Typography fontWeight="bold">{evidence.id}</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{evidence.description}</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(evidence.references || []).map((url, idx) => (
                            <Tooltip key={idx} title={url}>
                                <Chip
                                    size="small"
                                    label={`Ref ${idx + 1}`}
                                    icon={<ExternalLink size={14} />}
                                    onClick={() => window.open(url, '_blank')}
                                    clickable
                                />
                            </Tooltip>
                        ))}
                        {(!evidence.references || evidence.references.length === 0) && (
                            <Chip size="small" label="No valid references" color="warning" />
                        )}
                    </Box>
                </Paper>
            ))}
        </Stack>
    );

    const renderReviewStep = () => {
        const approvedModels = proposedModels.filter(m => selectedModels[m.id]);
        return (
            <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                    Review your MEL before creating it.
                </Typography>
                <Paper sx={{ p: 2 }} elevation={1}>
                    <Typography variant="h6">{title || topic.substring(0, 50)}</Typography>
                    <Typography variant="body2" color="text.secondary">{topic}</Typography>
                </Paper>
                <Typography variant="subtitle2">Models ({approvedModels.length})</Typography>
                {approvedModels.map(m => (
                    <Chip key={m.id} label={m.id} color="primary" variant="outlined" />
                ))}
                <Typography variant="subtitle2">Evidences ({evidencesWithRefs.length})</Typography>
                {evidencesWithRefs.map(e => (
                    <Chip key={e.id} label={e.id} color="secondary" variant="outlined" />
                ))}
            </Stack>
        );
    };

    // Render action buttons based on step
    const renderActions = () => {
        const commonProps = { disabled: loading };

        switch (activeStep) {
            case 0:
                return (
                    <Button {...commonProps} variant="contained" onClick={handleGenerateModels}
                        startIcon={loading ? <CircularProgress size={20} /> : <Brain size={20} />}>
                        {loading ? 'Generating...' : 'Generate Models'}
                    </Button>
                );
            case 1:
                return (
                    <>
                        {getRejectedCount(proposedModels, selectedModels) > 0 && (
                            <Button {...commonProps} onClick={handleRegenerateModels}
                                startIcon={<RefreshCw size={18} />}>
                                Regenerate Rejected
                            </Button>
                        )}
                        <Button {...commonProps} variant="contained" onClick={handleGenerateEvidences}
                            startIcon={loading ? <CircularProgress size={20} /> : <Check size={18} />}>
                            {loading ? 'Generating...' : 'Approve & Continue'}
                        </Button>
                    </>
                );
            case 2:
                return (
                    <>
                        {getRejectedCount(proposedEvidences, selectedEvidences) > 0 && (
                            <Button {...commonProps} onClick={handleRegenerateEvidences}
                                startIcon={<RefreshCw size={18} />}>
                                Regenerate Rejected
                            </Button>
                        )}
                        <Button {...commonProps} variant="contained" onClick={handleGenerateReferences}
                            startIcon={loading ? <CircularProgress size={20} /> : <Check size={18} />}>
                            {loading ? 'Generating...' : 'Approve & Get References'}
                        </Button>
                    </>
                );
            case 3:
                return (
                    <Button {...commonProps} variant="contained" onClick={() => setActiveStep(4)}
                        startIcon={<Check size={18} />}>
                        Continue to Review
                    </Button>
                );
            case 4:
                return (
                    <Button {...commonProps} variant="contained" color="success" onClick={handleAssembleMEL}
                        startIcon={loading ? <CircularProgress size={20} /> : <Brain size={20} />}>
                        {loading ? 'Creating...' : 'Create MEL'}
                    </Button>
                );
            default:
                return null;
        }
    };

    return (
        <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Brain size={24} />
                MEL Generation Wizard
            </DialogTitle>
            <DialogContent>
                <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
                    {STEPS.map((label) => (
                        <Step key={label}><StepLabel>{label}</StepLabel></Step>
                    ))}
                </Stepper>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Box sx={{ minHeight: 300, maxHeight: 400, overflow: 'auto' }}>
                    {renderStepContent()}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleClose} disabled={loading}>Cancel</Button>
                {activeStep > 0 && activeStep < 4 && (
                    <Button onClick={() => setActiveStep(prev => prev - 1)} disabled={loading}>
                        Back
                    </Button>
                )}
                {renderActions()}
            </DialogActions>
        </Dialog>
    );
};

export default MELWizard;

